import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Search, Loader2, Users, FileText, Video, Layers, Lock, MessageSquare, Play } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import FollowButton from "@/components/FollowButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { people: [], posts: [], videos: [], pages: [], groups: [] };

function PersonRow({ u }) {
  return (
    <div className="flex items-center gap-3 p-3" data-testid={`search-person-${u.user_id}`}>
      <Link to={`/user/${u.user_id}`}><Avatar className="w-11 h-11"><AvatarImage src={u.picture} /><AvatarFallback>{u.name?.[0]}</AvatarFallback></Avatar></Link>
      <div className="flex-1 min-w-0">
        <Link to={`/user/${u.user_id}`} className="text-sm font-semibold truncate flex items-center gap-1 hover:underline">{u.name} {u.is_private && <Lock className="w-3 h-3 text-muted-foreground" />}</Link>
        <div className="text-xs text-muted-foreground truncate">{u.username ? `@${u.username} · ` : ""}{u.followers_count} seguidores{u.bio ? ` · ${u.bio}` : ""}</div>
      </div>
      <FollowButton userId={u.user_id} />
      <Button asChild size="icon" variant="ghost" className="rounded-full w-8 h-8"><Link to={`/chat/${u.user_id}`}><MessageSquare className="w-4 h-4" /></Link></Button>
    </div>
  );
}

function PostRow({ p }) {
  const img = p.image_urls?.[0] || p.image_url;
  return (
    <Link to={`/post/${p.post_id}`} className="flex items-start gap-3 p-3 hover:bg-muted/40 transition-colors" data-testid={`search-post-${p.post_id}`}>
      <Avatar className="w-9 h-9 mt-0.5"><AvatarImage src={p.author_picture} /><AvatarFallback>{p.author_name?.[0]}</AvatarFallback></Avatar>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-muted-foreground">{p.author_name} · {new Date(p.created_at).toLocaleDateString("pt-BR")}</div>
        <p className="text-sm line-clamp-2 whitespace-pre-wrap">{p.content}</p>
        <div className="text-[11px] text-muted-foreground mt-1">{(p.likes || []).length} curtidas · {(p.comments || []).length} comentários</div>
      </div>
      {p.video_url ? (
        <div className="relative w-16 h-16 rounded-lg bg-black flex items-center justify-center shrink-0 overflow-hidden">{p.cover_url && <img src={p.cover_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-80" />}<Play className="relative w-5 h-5 text-white" /></div>
      ) : img ? <img src={img} alt="" className="w-16 h-16 rounded-lg object-cover shrink-0" /> : null}
    </Link>
  );
}

function PageRow({ pg }) {
  return (
    <Link to={`/pages/${pg.page_id}`} className="flex items-center gap-3 p-3 hover:bg-muted/40 transition-colors" data-testid={`search-page-${pg.page_id}`}>
      <Avatar className="w-11 h-11 rounded-xl"><AvatarImage src={pg.avatar_url || pg.cover_url} /><AvatarFallback className="rounded-xl"><Layers className="w-4 h-4" /></AvatarFallback></Avatar>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold truncate">{pg.name}</div>
        <div className="text-xs text-muted-foreground truncate">{pg.type?.replace("_", " ")} · {pg.followers_count || 0} seguidores{pg.description ? ` · ${pg.description}` : ""}</div>
      </div>
    </Link>
  );
}

const Empty = ({ text }) => <div className="text-sm text-muted-foreground text-center py-10" data-testid="search-empty">{text}</div>;

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [q, setQ] = useState(params.get("q") || "");
  const [tab, setTab] = useState(params.get("tab") || "all");
  const [res, setRes] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setRes(EMPTY); return; }
    setLoading(true);
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/search`, { params: { q: term, type: "all", limit: 30 }, signal: ctrl.signal });
        setRes({ ...EMPTY, ...r.data });
        setParams({ q: term, tab }, { replace: true });
      } catch (e) { if (!axios.isCancel(e)) setRes(EMPTY); }
      setLoading(false);
    }, 350);
    return () => { clearTimeout(t); ctrl.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const total = res.people.length + res.posts.length + res.videos.length + res.pages.length + res.groups.length;
  const counts = { people: res.people.length, posts: res.posts.length, videos: res.videos.length, pages: res.pages.length + res.groups.length };

  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid="search-page">
      <Card className="p-4">
        <h1 className="text-xl font-bold tracking-tight mb-3">Pesquisar</h1>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pessoas, publicações, vídeos, páginas e grupos..." className="pl-9 rounded-full h-11" data-testid="global-search-input" />
          {loading && <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />}
        </div>
        {q.trim().length >= 2 && !loading && <p className="text-xs text-muted-foreground mt-2" data-testid="search-total">{total} resultado{total === 1 ? "" : "s"} para "{q.trim()}"</p>}
      </Card>

      {q.trim().length < 2 ? (
        <Empty text="Digite pelo menos 2 letras para pesquisar." />
      ) : (
        <Tabs value={tab} onValueChange={(v) => { setTab(v); setParams({ q: q.trim(), tab: v }, { replace: true }); }}>
          <TabsList className="rounded-full flex-wrap h-auto">
            <TabsTrigger value="all" className="rounded-full" data-testid="search-tab-all">Tudo</TabsTrigger>
            <TabsTrigger value="people" className="rounded-full gap-1" data-testid="search-tab-people"><Users className="w-3.5 h-3.5" /> Pessoas ({counts.people})</TabsTrigger>
            <TabsTrigger value="posts" className="rounded-full gap-1" data-testid="search-tab-posts"><FileText className="w-3.5 h-3.5" /> Publicações ({counts.posts})</TabsTrigger>
            <TabsTrigger value="videos" className="rounded-full gap-1" data-testid="search-tab-videos"><Video className="w-3.5 h-3.5" /> Vídeos ({counts.videos})</TabsTrigger>
            <TabsTrigger value="pages" className="rounded-full gap-1" data-testid="search-tab-pages"><Layers className="w-3.5 h-3.5" /> Páginas & Grupos ({counts.pages})</TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="mt-3 space-y-3">
            {total === 0 && !loading && <Empty text="Nada encontrado." />}
            {res.people.length > 0 && <Card className="divide-y divide-border/60" data-testid="search-section-people"><div className="px-3 pt-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Pessoas</div>{res.people.slice(0, 5).map((u) => <PersonRow key={u.user_id} u={u} />)}{res.people.length > 5 && <button className="w-full p-2 text-xs text-primary hover:underline" onClick={() => setTab("people")}>Ver todas as pessoas</button>}</Card>}
            {res.posts.length > 0 && <Card className="divide-y divide-border/60" data-testid="search-section-posts"><div className="px-3 pt-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Publicações</div>{res.posts.slice(0, 5).map((p) => <PostRow key={p.post_id} p={p} />)}</Card>}
            {res.videos.length > 0 && <Card className="divide-y divide-border/60" data-testid="search-section-videos"><div className="px-3 pt-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Vídeos</div>{res.videos.slice(0, 5).map((p) => <PostRow key={p.post_id} p={p} />)}</Card>}
            {(res.pages.length + res.groups.length) > 0 && <Card className="divide-y divide-border/60" data-testid="search-section-pages"><div className="px-3 pt-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Páginas & Grupos</div>{[...res.pages, ...res.groups].slice(0, 5).map((pg) => <PageRow key={pg.page_id} pg={pg} />)}</Card>}
          </TabsContent>
          <TabsContent value="people" className="mt-3"><Card className="divide-y divide-border/60">{res.people.length ? res.people.map((u) => <PersonRow key={u.user_id} u={u} />) : <Empty text="Nenhuma pessoa encontrada." />}</Card></TabsContent>
          <TabsContent value="posts" className="mt-3"><Card className="divide-y divide-border/60">{res.posts.length ? res.posts.map((p) => <PostRow key={p.post_id} p={p} />) : <Empty text="Nenhuma publicação encontrada." />}</Card></TabsContent>
          <TabsContent value="videos" className="mt-3"><Card className="divide-y divide-border/60">{res.videos.length ? res.videos.map((p) => <PostRow key={p.post_id} p={p} />) : <Empty text="Nenhum vídeo encontrado." />}</Card></TabsContent>
          <TabsContent value="pages" className="mt-3 space-y-3">
            <Card className="divide-y divide-border/60"><div className="px-3 pt-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Páginas</div>{res.pages.length ? res.pages.map((pg) => <PageRow key={pg.page_id} pg={pg} />) : <Empty text="Nenhuma página encontrada." />}</Card>
            <Card className="divide-y divide-border/60"><div className="px-3 pt-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Grupos</div>{res.groups.length ? res.groups.map((pg) => <PageRow key={pg.page_id} pg={pg} />) : <Empty text="Nenhum grupo encontrado." />}</Card>
          </TabsContent>
        </Tabs>
      )}
      <div className="text-center"><Button variant="ghost" size="sm" onClick={() => navigate(-1)}>Voltar</Button></div>
    </div>
  );
}
